import { motion } from 'framer-motion'
import { Dumbbell, Clock, UserCheck, Monitor, ArrowRight } from 'lucide-react'

export default function Gym() {
  const features = [
    { icon: UserCheck, title: '人脸识别入场', desc: '刷脸进门，无需携带任何卡片' },
    { icon: Dumbbell, title: '智能设备联动', desc: '跑步机、力量器械数据自动上传' },
    { icon: Monitor, title: 'SaaS 管理后台', desc: '老板随时查看经营数据' },
    { icon: Clock, title: '24小时营业', desc: '无人值守，降低人工成本' },
  ]

  return (
    <div className="min-h-screen pt-16">
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] opacity-15"
          style={{ background: 'radial-gradient(ellipse, rgba(236,72,153,0.5), transparent 70%)', filter: 'blur(60px)' }} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium mb-6"
              style={{ background: 'rgba(236,72,153,0.15)', border: '1px solid rgba(236,72,153,0.3)', color: '#f472b6' }}>
              <div className="w-2 h-2 rounded-full bg-pink-400 animate-pulse" />
              智能健身
            </div>

            <h1 className="text-4xl md:text-6xl font-black text-white mb-6 tracking-tight">
              Lafit连锁智能健身房
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
              人脸识别门禁 + 智能器械 + 云端管理，24 小时无人值守健身房
            </p>

            <a
              href="https://gzzh.lafit.club"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-white transition-all duration-300 hover:gap-3"
              style={{ background: '#ec4899', boxShadow: '0 0 30px rgba(236,72,153,0.4)' }}
            >
              了解更多
              <ArrowRight className="w-5 h-5" />
            </a>
          </motion.div>

          {/* Demo */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-16 max-w-lg mx-auto"
          >
            <div className="relative rounded-2xl overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(15,15,30,0.9), rgba(20,20,50,0.9))',
                border: '1px solid rgba(236,72,153,0.2)',
                boxShadow: '0 0 60px rgba(236,72,153,0.15), 0 40px 80px rgba(0,0,0,0.5)',
              }}
            >
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-400" />
                  <span className="text-xs text-gray-400 font-mono">智能健身房 · 运营中</span>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl" style={{ background: 'rgba(236,72,153,0.1)', border: '1px solid rgba(236,72,153,0.2)' }}>
                    <div className="text-2xl font-bold text-white">24h</div>
                    <div className="text-xs text-gray-500">营业时间</div>
                  </div>
                  <div className="p-4 rounded-xl" style={{ background: 'rgba(236,72,153,0.1)', border: '1px solid rgba(236,72,153,0.2)' }}>
                    <div className="text-2xl font-bold text-white">128</div>
                    <div className="text-xs text-gray-500">今日入场</div>
                  </div>
                </div>
                <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <UserCheck className="w-4 h-4 text-pink-400" />
                    <span className="text-sm text-white">入场方式</span>
                  </div>
                  <div className="text-xs text-gray-500">人脸识别 / 扫码 / 刷卡</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 relative">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">核心功能</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">全套智能解决方案，助力健身房降本增效</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="p-6 rounded-2xl text-center"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4"
                  style={{ background: 'rgba(236,72,153,0.15)', color: '#f472b6' }}>
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-400">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(236,72,153,0.1), rgba(168,85,247,0.1))' }} />
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-black text-white mb-6">
              打造下一代智能健身房
            </h2>
            <p className="text-gray-400 mb-8 max-w-xl mx-auto">
              联系我们，了解加盟详情
            </p>
            <a
              href="https://gzzh.lafit.club"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-white transition-all duration-300 hover:gap-3"
              style={{ background: '#ec4899', boxShadow: '0 0 30px rgba(236,72,153,0.4)' }}
            >
              访问官网
              <ArrowRight className="w-5 h-5" />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
